import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';
import { 
  Home, 
  MessageSquare, 
  User, 
  Settings, 
  LogOut,
  Menu,
  Briefcase,
  Plus,
  FileText
} from 'lucide-react';

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', label: t('nav.home'), icon: Home },
    { path: '/messages', label: t('nav.messages'), icon: MessageSquare },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="h-16 bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="h-full max-w-screen-2xl mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <div className="w-9 h-9 bg-slate-900 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold">W</span>
          </div>
          <span className="font-bold text-lg text-slate-900 hidden sm:block">WorkMatch</span>
        </button>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.path}
              data-testid={`nav-${item.path.slice(1)}-btn`}
              onClick={() => navigate(item.path)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isActive(item.path)
                  ? 'bg-slate-100 text-slate-900'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Post Job button for employers */}
          {user?.role === 'employer' && (
            <Button
              data-testid="nav-create-job-btn"
              onClick={() => navigate('/jobs/create')}
              className="hidden sm:flex bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('jobs.create_job')}
            </Button>
          )}

          <NotificationBell />
          <LanguageSwitcher />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                data-testid="user-menu-btn"
                className="flex items-center gap-2 p-1 rounded-full hover:bg-slate-100 transition-colors"
              >
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user?.photo_url} alt={user?.name} />
                  <AvatarFallback className="bg-slate-200 text-slate-700">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="font-medium text-slate-900">{user?.name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
                <span className="inline-block mt-1 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full capitalize">
                  {user?.role}
                </span>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                data-testid="menu-profile-btn"
                onClick={() => navigate('/profile')}
                className="cursor-pointer"
              >
                <User className="w-4 h-4 mr-2" />
                {t('nav.profile')}
              </DropdownMenuItem>
              <DropdownMenuItem 
                data-testid="menu-myjobs-btn"
                onClick={() => navigate('/profile')}
                className="cursor-pointer"
              >
                <Briefcase className="w-4 h-4 mr-2" />
                {t('jobs.my_jobs')}
              </DropdownMenuItem>
              <DropdownMenuItem 
                data-testid="menu-settings-btn"
                onClick={() => navigate('/settings')}
                className="cursor-pointer"
              >
                <Settings className="w-4 h-4 mr-2" />
                {t('nav.settings')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                data-testid="menu-logout-btn"
                onClick={handleLogout}
                className="cursor-pointer text-red-600 focus:text-red-600"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 md:hidden z-50 pb-safe">
        <div className="flex items-center justify-around py-2">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg min-w-[64px] ${
                isActive(item.path) ? 'text-emerald-600' : 'text-slate-500'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs font-medium">{item.label}</span>
            </button>
          ))}
          <button
            onClick={() => navigate('/profile')}
            className={`flex flex-col items-center gap-1 p-2 rounded-lg min-w-[64px] ${
              isActive('/profile') ? 'text-emerald-600' : 'text-slate-500'
            }`}
          >
            <User className="w-5 h-5" />
            <span className="text-xs font-medium">{t('nav.profile')}</span>
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
